import {gql} from '@apollo/client';
import {compact} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import FragmentWrapper from 'graphql-client/FragmentWrapper';
import DropdownFilterForm, {getFilterText} from 'search/filters/base/DropdownFilterForm';
import {FilterKey, GenericFilter, SingleValueFilter} from 'search/SearchConstants';
import {OptionData} from 'ui-library/menus/MenuConstants';
import pickDefined from 'util/pickDefined';

import {TeamFilterTeam as GeneratedTeamFilterTeam} from './__generated/TeamFilterTeam';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';

export const TEAM_FILTER_TEAM_FRAGMENT = gql`
  fragment TeamFilterTeam on TeamNode {
    id
    name
  }
`;

export const TeamFilterTeamFragment: FragmentWrapper<TeamFilterTeam> = {
  fragmentName: 'TeamFilterTeamFragment',
  fragment: TEAM_FILTER_TEAM_FRAGMENT,
};

export type TeamFilterTeam = Omit<GeneratedTeamFilterTeam, '__typename'>;

interface TeamFilterFormProps extends BaseFilterFormProps {
  filter: GenericFilter<FilterKey.TEAM>;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: GenericFilter<FilterKey.TEAM>) => void;
  onRemove?: () => void;
  teams: Array<TeamFilterTeam>;
}

export interface TeamFilterFormExtraProps {
  teams: Array<TeamFilterTeam>;
  shouldIncludeNone?: boolean;
}

export const NONE_FILTER = {
  name: 'No teams',
  value: 'none',
};

const ALL_FILTER = {name: 'Any team', value: null};

function getActiveFilterText(optionDatas: Array<OptionData>, filter: SingleValueFilter): string {
  if (filter.value == null) {
    return 'Team';
  }

  let textToPrefix = getFilterText(optionDatas, filter);

  if (filter.value === NONE_FILTER.value) {
    textToPrefix = 'None';
  }

  return `Team: ${textToPrefix}`;
}

export default function TeamFilterForm(props: TeamFilterFormProps & TeamFilterFormExtraProps): JSX.Element {
  const baseProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  const teams = props.teams.map((team) => ({
    name: team.name,
    value: team.id,
    icon: 'team',
  }));
  const optionDatas = compact<OptionData>([
    ALL_FILTER,
    props.shouldIncludeNone ? NONE_FILTER : undefined,
    ..._.sortBy(teams, (team) => team.name.toLowerCase()),
  ]);

  return (
    <DropdownFilterForm
      {...baseProps}
      dropdownToggleText={getActiveFilterText(optionDatas, props.filter)}
      filterStyle={props.filterStyle}
      isDisabled={teams.length === 0}
      isMulti={false}
      isSearchable={true}
      onChangeValue={(value: Array<string>) => {
        // @ts-expect-error BNCH(56464): Fix this. TEAM is a SingleValueFilter in `SearchConstants.ts`,
        // but here we are trying to assign an array of strings.
        props.onChangeFilter({...props.filter, value});
      }}
      onRemove={props.onRemove}
      optionDatas={optionDatas}
      value={props.filter.value}
    />
  );
}
